import { Button, ButtonProps, CircularProgress } from '@mui/material';
import { createUseStyles } from 'react-jss';
import { colors, hexToRgbA } from '../../theme/colors';
import spacing, { isMobileScreen } from '../../theme/spacing';
import { fontSize } from '../../theme/typography';
import { CSSProperties, ReactNode } from 'react';
import { borderRadius } from '../../theme/borderRadius';

type TypeStyle =
  | 'primary'
  | 'secondary'
  | 'secondaryLight'
  | 'secondaryDark'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'light'
  | 'link'
  | 'primaryLight';

export interface MainButtonProps extends ButtonProps {
  children?: ReactNode;
  style?: CSSProperties;
  contentStyle?: CSSProperties;
  textStyle?: CSSProperties;
  text?: string | null;
  onClick?: (e?: any) => void;
  onPress?: (e?: any) => void;
  typeStyle?: TypeStyle;
  uppercase?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'text' | 'outlined' | 'contained';
  disabled?: boolean;
  isLoading?: boolean;
  fullWidth?: boolean;
  height?: number;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
}

const useStyles = createUseStyles({
  root: {
    minWidth: 10,
  },
});

const typeColors: Record<TypeStyle, string> = {
  primary: colors.primary,
  primaryLight: colors.primaryVeryLight,
  secondaryLight: colors.textPrimary,
  secondary: colors.backgroundSuperLight,
  secondaryDark: hexToRgbA(colors.backgroundSuperLight, 0.25),
  success: colors.green,
  danger: colors.red,
  warning: colors.yellow,
  info: colors.backgroundSuperLight,
  light: colors.backgroundSuperLight,
  link: 'transparent',
};

const getTypeColor = (typeStyle?: TypeStyle): string | undefined => {
  return typeStyle ? typeColors[typeStyle] : undefined;
};

const outlinedBorderColors: Record<TypeStyle, string> = {
  primary: colors.primary,
  primaryLight: colors.primaryVeryLight,
  secondaryLight: colors.backgroundSuperLight,
  secondary: colors.backgroundSuperLight,
  secondaryDark: colors.backgroundSuperLight,
  success: colors.green,
  danger: colors.red,
  warning: colors.yellow,
  info: colors.backgroundSuperLight,
  light: colors.backgroundSuperLight,
  link: 'transparent',
};

export const MainButton = ({
  typeStyle,
  children,
  style,
  contentStyle,
  textStyle,
  text,
  onClick,
  onPress,
  uppercase,
  size,
  variant,
  disabled,
  isLoading = false,
  fullWidth,
  height,
  startIcon,
  endIcon,
  ...props
}: MainButtonProps) => {
  const classes = useStyles();

  const typeColor = getTypeColor(typeStyle);
  const isDisabled = Boolean(disabled || isLoading);
  const handleClick = onClick || onPress;

  const backgroundColor = () => {
    if (variant === 'outlined' || variant === 'text') {
      return 'transparent';
    }

    if (typeColor) {
      return typeColor;
    }

    return colors.primary;
  };

  const textColor = () => {
    if (typeStyle === 'secondaryLight') {
      if (variant === 'outlined') {
        return colors.textPrimary;
      }
      return colors.backgroundMedium;
    }

    if (typeStyle === 'secondary' || typeStyle === 'secondaryDark') {
      return colors.textPrimary;
    }

    if (variant === 'outlined') {
      return getTypeColor(typeStyle);
    }

    if (typeStyle === 'link') {
      return colors.textPrimary;
    }

    if (typeStyle === 'light') {
      return colors.textPrimary;
    }

    return colors.backgroundMedium;
  };

  const textFontSize = () => {
    if (isMobileScreen) {
      return size === 'large' ? fontSize.md : fontSize.sm;
    }

    return size === 'large' ? fontSize.lg : fontSize.md;
  };

  const paddingHorizontal = () => {
    if (size === 'small') {
      return {
        paddingLeft: spacing.smallMedium,
        paddingRight: spacing.smallMedium,
      };
    }

    if (size === 'large') {
      return {
        paddingLeft: spacing.mediumPlus,
        paddingRight: spacing.mediumPlus,
      };
    }

    return {
      paddingLeft: spacing.medium,
      paddingRight: spacing.medium,
    };
  };

  return (
    <Button
      {...props}
      onClick={handleClick}
      disabled={isDisabled}
      fullWidth={fullWidth}
      startIcon={isLoading ? undefined : startIcon}
      endIcon={isLoading ? undefined : endIcon}
      disableElevation
      variant={variant || 'contained'}
      classes={{
        root: classes.root,
      }}
      style={{
        color: style?.color || textColor(),
        textTransform: uppercase ? 'uppercase' : 'none',
        background:
          style?.backgroundColor || style?.background || backgroundColor(),
        borderColor:
          style?.borderColor ||
          (variant === 'outlined' && typeStyle
            ? outlinedBorderColors[typeStyle]
            : typeColor) ||
          colors.primaryMedium,
        borderRadius: borderRadius.large,
        opacity: isDisabled ? 0.5 : 1,
        fontSize: textFontSize(),
        height,
        ...paddingHorizontal(),
        ...style,
      }}
    >
      {isLoading ? (
        <CircularProgress
          size={textFontSize()}
          style={{ color: textColor() }}
        />
      ) : (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing.small,
            ...contentStyle,
            ...textStyle,
          }}
        >
          {children ?? text}
        </span>
      )}
    </Button>
  );
};
